// Collection Context Management
// Handles storing and retrieving collection context for breadcrumb navigation

class CollectionContextManager {
  constructor() {
    this.storageKey = 'productCollectionContext';
    this.maxAge = 30 * 60 * 1000; // 30 minutes
  }

  // Store collection context when navigating from collection to product
  storeContext(handle, title, url) {
    try {
      const context = {
        handle: handle,
        title: title,
        url: url,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(context));
      
      // Also add to URL as fallback
      this.addToURL(handle, title, url);
    } catch (error) {
      console.warn('Could not store collection context:', error);
    }
  }

  // Retrieve collection context
  getContext() {
    try {
      // First try sessionStorage
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        const context = JSON.parse(stored);
        if (this.isValid(context)) {
          return context;
        } else {
          // Remove expired context
          sessionStorage.removeItem(this.storageKey);
        }
      }

      // Fallback to URL parameters
      return this.getFromURL();
    } catch (error) {
      console.warn('Error retrieving collection context:', error);
      return null;
    }
  }

  // Check if context is still valid
  isValid(context) {
    if (!context || !context.timestamp) return false;
    return (Date.now() - context.timestamp) < this.maxAge;
  }

  // Clear context
  clearContext() {
    try {
      sessionStorage.removeItem(this.storageKey);
      this.removeFromURL();
    } catch (error) {
      console.warn('Could not clear collection context:', error);
    }
  }

  // Add collection info to URL parameters
  addToURL(handle, title, url) {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('from_collection', handle);
      url.searchParams.set('from_collection_title', encodeURIComponent(title));
      url.searchParams.set('from_collection_url', encodeURIComponent(url));
      window.history.replaceState({}, '', url);
    }
  }

  // Get collection info from URL parameters
  getFromURL() {
    const url = new URL(window.location);
    const handle = url.searchParams.get('from_collection');
    const title = url.searchParams.get('from_collection_title');
    const collectionUrl = url.searchParams.get('from_collection_url');

    if (handle && title && collectionUrl) {
      return {
        handle: handle,
        title: decodeURIComponent(title),
        url: decodeURIComponent(collectionUrl),
        timestamp: Date.now()
      };
    }
    return null;
  }

  // Remove collection info from URL parameters
  removeFromURL() {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.delete('from_collection');
      url.searchParams.delete('from_collection_title');
      url.searchParams.delete('from_collection_url');
      window.history.replaceState({}, '', url);
    }
  }

  // Update breadcrumb with collection context (without clearing it)
  updateBreadcrumb() {
    const context = this.getContext();
    if (!context) return;

    const breadcrumbNav = document.querySelector('nav[aria-label="Breadcrumb"]');
    if (!breadcrumbNav) return;

    const breadcrumbList = breadcrumbNav.querySelector('ol');
    if (!breadcrumbList) return;

    // Check if collection breadcrumb already exists
    const existingCollectionItem = breadcrumbList.querySelector('[data-collection-context]');
    if (existingCollectionItem) return;

    // Find the home link (first item)
    const homeItem = breadcrumbList.querySelector('li:first-child');
    if (!homeItem) return;

    // Create collection breadcrumb item
    const collectionItem = document.createElement('li');
    collectionItem.className = 'flex items-center';
    collectionItem.setAttribute('data-collection-context', context.handle);
    
    const separator = breadcrumbNav.querySelector('.mx-2')?.textContent || '>';
    
    collectionItem.innerHTML = `
      <span class="mx-2">${separator}</span>
      <a href="${context.url}" class="link-animated-underline text-black title-style1 line-clamp-1">
        ${context.title}
      </a>
    `;
    
    // Insert collection item after home item
    homeItem.parentNode.insertBefore(collectionItem, homeItem.nextSibling);
    
    // Context is NOT cleared here - it persists until page navigation
  }
}

// Initialize global instance
window.collectionContextManager = new CollectionContextManager();

// Auto-clear context on certain pages
document.addEventListener('DOMContentLoaded', function() {
  const path = window.location.pathname;
  
  // Clear context when navigating to collection pages or home
  if (path.includes('/collections/') && !path.includes('/products/') ||
      path === '/' || 
      path === '/shop') {
    window.collectionContextManager.clearContext();
  }
  
  // Update breadcrumb for product pages without collection context
  if (path.includes('/products/')) {
    window.collectionContextManager.updateBreadcrumb();
  }
}); 